import {
  CENTER_X,
  CENTER_Y,
  CURVE_STEPS,
  LOGO_PATHS,
  ORBIT_DOTS,
  ORBIT_RADIUS,
  PATTERN_INTRO_DURATION,
  PATTERN_STEP_DURATION,
  POINT_COUNT,
} from './shared-ui-logo-loading.consts';
import type {
  Circle,
  LogoFrame,
  LogoGeometry,
  Point,
  Shape,
} from './shared-ui-logo-loading.types';

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

export const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;

export const rampDegrees = (
  t: number,
  start: number,
  end: number,
  degrees: number
) => degrees * easeInOutSine(clamp01((t - start) / (end - start)));

const mean = (shape: Shape, axis: 0 | 1) =>
  shape.reduce((sum, point) => sum + point[axis], 0) / shape.length;

const rotateStart = (shape: Shape, start: number) =>
  shape.slice(start).concat(shape.slice(0, start));

export function resample(points: Shape, count: number): Shape {
  const lengths = [0];
  for (let i = 1; i <= points.length; i++) {
    const a = points[i - 1];
    const b = points[i % points.length];
    lengths.push(lengths[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }

  const total = lengths[lengths.length - 1];
  const out: Shape = [];
  let segment = 0;
  for (let k = 0; k < count; k++) {
    const distance = (total * k) / count;
    while (lengths[segment + 1] < distance) segment++;
    const a = points[segment];
    const b = points[(segment + 1) % points.length];
    const s =
      (distance - lengths[segment]) /
      (lengths[segment + 1] - lengths[segment] || 1);
    out.push([a[0] + (b[0] - a[0]) * s, a[1] + (b[1] - a[1]) * s]);
  }
  return out;
}

export function normalizeShape(points: Shape): Shape {
  const shape = resample(points, POINT_COUNT);

  let area = 0;
  for (let i = 0; i < POINT_COUNT; i++) {
    const a = shape[i];
    const b = shape[(i + 1) % POINT_COUNT];
    area += a[0] * b[1] - b[0] * a[1];
  }
  if (area < 0) shape.reverse();

  const cx = mean(shape, 0);
  const cy = mean(shape, 1);
  let top = 0;
  let topScore = -Infinity;
  shape.forEach(([x, y], index) => {
    const score = (cy - y) / (Math.hypot(x - cx, y - cy) || 1);
    if (score > topScore) {
      topScore = score;
      top = index;
    }
  });
  return rotateStart(shape, top);
}

function circleShape(cx: number, cy: number, radius: number): Shape {
  const points: Shape = [];
  for (let i = 0; i < 240; i++) {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / 240;
    points.push([cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)]);
  }
  return normalizeShape(points);
}

function alignShape(reference: Shape, target: Shape): Shape {
  const ax = mean(reference, 0);
  const ay = mean(reference, 1);
  const bx = mean(target, 0);
  const by = mean(target, 1);

  let best = 0;
  let bestError = Infinity;
  for (let offset = 0; offset < POINT_COUNT; offset++) {
    let error = 0;
    for (let n = 0; n < POINT_COUNT; n += 2) {
      const a = reference[n];
      const b = target[(n + offset) % POINT_COUNT];
      error += (a[0] - ax - b[0] + bx) ** 2 + (a[1] - ay - b[1] + by) ** 2;
    }
    if (error < bestError) {
      bestError = error;
      best = offset;
    }
  }
  return rotateStart(target, best);
}

const cubicPoint = (
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
  t: number
): Point => {
  const u = 1 - t;
  const w0 = u * u * u;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t * t * t;
  return [
    w0 * p0[0] + w1 * p1[0] + w2 * p2[0] + w3 * p3[0],
    w0 * p0[1] + w1 * p1[1] + w2 * p2[1] + w3 * p3[1],
  ];
};

export function flattenPath(d: string): Shape {
  const tokens = d.match(/[a-z]|-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi) ?? [];
  const points: Shape = [];
  let command = '';
  let index = 0;
  const read = (): Point => [Number(tokens[index++]), Number(tokens[index++])];

  while (index < tokens.length) {
    const token = tokens[index];
    if (/^[a-z]$/i.test(token)) {
      if (!/^[MLCZ]$/.test(token)) {
        throw new Error(`Unsupported path command "${token}"`);
      }
      command = token;
      index++;
      continue;
    }

    if (command === 'C') {
      const start = points[points.length - 1];
      const [c1, c2, end] = [read(), read(), read()];
      for (let step = 1; step <= CURVE_STEPS; step++) {
        points.push(cubicPoint(start, c1, c2, end, step / CURVE_STEPS));
      }
    } else {
      points.push(read());
    }
  }
  return points;
}

export const orbitPoint = (angle: number): Point => [
  CENTER_X + ORBIT_RADIUS * Math.cos(toRadians(angle)),
  CENTER_Y + ORBIT_RADIUS * Math.sin(toRadians(angle)),
];

function buildLogoGeometry(): LogoGeometry {
  const logo = LOGO_PATHS.map((d) => normalizeShape(flattenPath(d)));
  const dots = ORBIT_DOTS.map(({ angle, radius }, index) => {
    const [x, y] = orbitPoint(angle);
    return alignShape(logo[index], circleShape(x, y, radius));
  });
  return { logo, dots, unitCircle: circleShape(0, 0, 1) };
}

let logoGeometry: LogoGeometry | undefined;

export function getLogoGeometry(): LogoGeometry {
  logoGeometry ??= buildLogoGeometry();
  return logoGeometry;
}

export function morphPath(
  from: Shape,
  to: Shape,
  progress: number,
  rotation: number
): string {
  const cos = Math.cos(toRadians(rotation));
  const sin = Math.sin(toRadians(rotation));
  let d = '';
  for (let n = 0; n < POINT_COUNT; n++) {
    const x = from[n][0] + (to[n][0] - from[n][0]) * progress - CENTER_X;
    const y = from[n][1] + (to[n][1] - from[n][1]) * progress - CENTER_Y;
    d += `${n ? 'L' : 'M'}${(CENTER_X + x * cos - y * sin).toFixed(2)} ${(CENTER_Y + x * sin + y * cos).toFixed(2)}`;
  }
  return `${d}Z`;
}

export function circlePath([x, y, radius]: Circle): string {
  const points = getLogoGeometry().unitCircle.map(
    ([a, b]) => `${(x + a * radius).toFixed(2)} ${(y + b * radius).toFixed(2)}`
  );
  return `M${points.join('L')}Z`;
}

export const lerp = (from: number, to: number, progress: number) =>
  from + (to - from) * progress;

function patternIntro(
  elapsed: number,
  index: number,
  rotation: number,
  firstFrame: Circle[]
): string {
  const { logo, dots } = getLogoGeometry();
  if (elapsed < 1) {
    const progress = easeInOutCubic(clamp01((elapsed - 0.3) / 0.7));
    return morphPath(logo[index], dots[index], progress, rotation);
  }

  const { angle, radius } = ORBIT_DOTS[index];
  const [x, y, targetRadius] = firstFrame[index];
  const [orbitX, orbitY] = orbitPoint(angle + rotation);
  const glide = easeInOutCubic(clamp01((elapsed - 1.3) / 0.8));
  return circlePath([
    lerp(orbitX, x, glide),
    lerp(orbitY, y, glide),
    lerp(radius, targetRadius, glide),
  ]);
}

export function createPatternFrame(
  pattern: (elapsed: number) => Circle[],
  cycles: number
): LogoFrame {
  const intro = PATTERN_INTRO_DURATION;
  const duration = cycles * PATTERN_STEP_DURATION;
  const total = intro * 2 + duration;

  return (elapsed) => {
    const loop = elapsed % total;
    const firstFrame = pattern(0);
    const circles = pattern(Math.min(duration, Math.max(0, loop - intro)));

    return circles.map((circle, index) => {
      if (loop < intro) {
        const rotation = rampDegrees(loop, 0.3, intro, 720);
        return patternIntro(loop, index, rotation, firstFrame);
      }
      if (loop < intro + duration) return circlePath(circle);

      const outro = total - loop;
      const rotation = 1440 - rampDegrees(outro, 0.3, intro, 720);
      return patternIntro(outro, index, rotation, firstFrame);
    });
  };
}
